import React, { useState, useEffect, useContext } from 'react';
import { Box, Stack, TextField, Button, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, CircularProgress } from '@mui/material';
import { styled } from '@mui/material/styles';
import Grid from '@mui/material/Grid';
import CloseIcon from '@mui/icons-material/Close';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { HomeContext } from '../utils/contexts/homeContext';
import {
    GetDayJSValueFromUTCDate, GetDateFromDayJSValue, PatientServicePaymentAPIUrl, GetNoToCurrencyFormat, GetNumberFromCurrencyFormat
} from '../utils/extensions/functionEx';
import dayjs from 'dayjs';
import { ServiceFormContext } from '../utils/contexts/serviceFormContext';
import AttachmentBox from '../shared/components/attachmentBox'
import { useCookies } from 'react-cookie';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialogContent-root': {
        padding: theme.spacing(2),
    },
    '& .MuiDialogActions-root': {
        padding: theme.spacing(1),
    },
    '& .MuiDialog-paper': {
        maxWidth: '640px'
    }
}));


export default function PaymentForm(props) {
    const { selectedPayment, handlePaymentFormCloseClick } = props;
    const { ShowAPIError, OpenErrorAlert, OpenSuccessAlert, homeState, ClearLoggedUser } = useContext(HomeContext);
    const { selectedPatientService, isPaymentFormOpen, SetIsShowPaymentForm, SetPageLoading, isPageLoadingOpen } = useContext(ServiceFormContext);

    const [cleared, setCleared] = useState(false);
    const [payDate, setPayDate] = useState(dayjs());
    const [receiptNo, setReceiptNo] = useState('');
    const [amount, setAmount] = useState(0);
    const [remarks, setRemarks] = useState('');
    const [attchment, setAttchment] = useState(null);
    const [removedDocIds, setRemovedDocIds] = useState([]);
    const [removedPayDocIds, setRemovedPayDocIds] = useState([]);

    const [cookies, setCookie] = useCookies(['hplus_home_care_user_session_code']);

    useEffect(() => {
        if (cleared) {
            const timeout = setTimeout(() => {
                setCleared(false);
            }, 1500);

            return () => clearTimeout(timeout);
        }
        return () => { };
    }, [cleared]);

    useEffect(() => {
        if (selectedPayment) {
            setPayDate(GetDayJSValueFromUTCDate(selectedPayment.pay_date));
            setAmount(GetNoToCurrencyFormat(selectedPayment.amount));
            setReceiptNo(selectedPayment.receipt_no);
            setRemarks(selectedPayment.remarks);

            if (selectedPayment.documents && selectedPayment.documents.length > 0) {

                const tempPayDoc = {
                    id: selectedPayment.documents[0].id,
                    uploadedFileId: selectedPayment.documents[0].document_id,
                    fileName: selectedPayment.documents[0].file_name,
                    documentType: selectedPayment.documents[0].document_type,
                    contentType: selectedPayment.documents[0].content_type
                };
                setAttchment(tempPayDoc);
            }

        }
    }, [selectedPayment]);

    const handleClose = () => {
        SetIsShowPaymentForm(false);
    };

    const handleSaveClick = async () => {
        try {
            if (!payDate || !receiptNo || receiptNo.toString().trim().length === 0 || !amount || amount.trim().length === 0) {
                OpenErrorAlert('Enter valid payment details.')
                return;
            }
            const tempAmount = GetNumberFromCurrencyFormat(amount);
            if (tempAmount <= 0) {
                OpenErrorAlert('Enter valid payment details.')
                return;
            }
            SetPageLoading(true);

            const tempPayment = {
                id: 0,
                service_id: selectedPatientService.service_id,
                receipt_no: receiptNo,
                pay_date: GetDateFromDayJSValue(payDate),
                amount: tempAmount,
                remarks: remarks.trim(),
                removed_doc_ids: [...removedDocIds],
                removed_pay_doc_ids: [...removedPayDocIds],
                documents: []
            };
            if (attchment && attchment.id === 0) {
                tempPayment.documents.push({
                    id: 0,
                    document_id: attchment.uploadedFileId,
                    document_type: attchment.documentType,
                    document_no: '',
                    notes: ''
                });
            }
            const headers = new Headers();
            headers.append("Accept", "application/json");
            headers.append("Accept-Encoding", "application/json");
            headers.append("Content-Type", "application/json");
            headers.append("Hc-App-Auth-Token", cookies.hplus_home_care_user_session_code);
            let response;

            if (selectedPayment && selectedPayment.id > 0) {
                tempPayment.id = selectedPayment.id;

                response = await fetch(PatientServicePaymentAPIUrl(homeState.loggedUser.companyCode), {
                    method: "PUT",
                    headers: headers,
                    body: JSON.stringify(tempPayment)
                });
            } else {
                response = await fetch(PatientServicePaymentAPIUrl(homeState.loggedUser.companyCode), {
                    method: "POST",
                    headers: headers,
                    body: JSON.stringify(tempPayment)
                });
            }

            if (response.status === 401) {
                OpenErrorAlert('User login history expired, please log in again.');
                setCookie('hplus_home_care_user_session_code', '', { path: '/' });
                ClearLoggedUser();
            } else if (response.ok) {
                OpenSuccessAlert('Patient payment saved successfully.');
                handlePaymentFormCloseClick();

                return;
            }
            ShowAPIError(response);
        }
        catch (error) {
            OpenErrorAlert(error)
        }
        finally {
            SetPageLoading(false);
        }
    }

    const handleDocSaveClick = (attachValue) => {
        setAttchment(attachValue);
    }

    const handleFileDeleteClick = () => {
        const tempRemovedDocIds = [...removedDocIds];
        const tempRemovedPayDocIds = [...removedPayDocIds];

        if (attchment.uploadedFileId > 0) {
            setRemovedDocIds([...tempRemovedDocIds, attchment.uploadedFileId]);
        }
        if (attchment.id > 0) {
            setRemovedPayDocIds([...tempRemovedPayDocIds, attchment.id]);
        }
        setAttchment(null);
    }


    return (
        <React.Fragment>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={"en-in"}>
                <BootstrapDialog
                    onClose={handleClose}
                    open={isPaymentFormOpen}
                >
                    <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title-service-payment-form">
                        Patient Payment
                    </DialogTitle>
                    <IconButton
                        aria-label="close"
                        onClick={handleClose}
                        sx={(theme) => ({
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: theme.palette.grey[500],
                        })}
                    >
                        <CloseIcon />
                    </IconButton>
                    <DialogContent dividers>
                        <Stack>
                            <Box>
                                <Grid container spacing={2}>
                                    <Grid size={4}>
                                        <DatePicker format="DD/MM/YYYY"
                                            label="Pay Date" maxDate={dayjs()}
                                            minDate={dayjs().add(-3, 'day')}
                                            value={payDate}
                                            onChange={(newValue) => { setPayDate(newValue); }}
                                            slotProps={{
                                                textField: {
                                                    size: "small", fullWidth: "true", required: "true",
                                                    error: !payDate ? true : false,
                                                    helperText: !payDate ? 'Enter payment date' : ''
                                                },
                                                field: {
                                                    clearable: true,
                                                    onClear: () => setCleared(true)
                                                }
                                            }}
                                        />
                                    </Grid>
                                    <Grid size={4}>
                                        <TextField size="small" fullWidth type='number'
                                            label="Receipt No"
                                            value={receiptNo}
                                            error={!receiptNo || receiptNo.toString().trim().length === 0 ? true : false}
                                            helperText={!receiptNo || receiptNo.toString().trim().length === 0 ? 'Invalid receipt no' : ''}
                                            onChange={(event) => {
                                                setReceiptNo(event.target.value);
                                            }}
                                        />
                                    </Grid>
                                    <Grid size={4}>
                                        <TextField size="small" fullWidth
                                            label="Amount"
                                            value={amount}
                                            error={amount <= 0 ? true : false}
                                            helperText={amount <= 0 ? 'Invalid amount' : ''}
                                            onChange={(event) => {
                                                setAmount(event.target.value);
                                            }}
                                            onBlur={(event) => {
                                                const tempFormValue = GetNoToCurrencyFormat(event.target.value ?? 0);
                                                setAmount(tempFormValue);
                                            }}
                                            onFocus={(event) => {
                                                if (amount > 0) {
                                                    const tempValue = parseFloat(event.target.value ?? 0);
                                                    if (tempValue > 0) {
                                                        setAmount(GetNumberFromCurrencyFormat(event.target.value));
                                                    } else {
                                                        setAmount('0');
                                                    }
                                                }
                                            }}
                                        />
                                    </Grid>

                                    <Grid size={12}>
                                        <TextField size="small" fullWidth
                                            label="Remarks"
                                            helperText=""
                                            value={remarks}
                                            onChange={(event) => {
                                                setRemarks(event.target.value);
                                            }}
                                        />
                                    </Grid>
                                    <Grid size={12}>
                                        <AttachmentBox selectedFile={attchment}
                                            handleSaveClick={handleDocSaveClick} handleDeleteClick={handleFileDeleteClick}
                                        />
                                    </Grid>
                                </Grid>
                            </Box>
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        {
                            isPageLoadingOpen && <CircularProgress size="30px" />
                        }
                        <Button variant="contained" onClick={handleSaveClick}>
                            Save
                        </Button>
                    </DialogActions>
                </BootstrapDialog> </LocalizationProvider>
        </React.Fragment>

    );
}
